import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  LinearProgress,
  Toolbar,
  IconButton,
  Stack,
  Chip,
  Tooltip,
  useMediaQuery,
  Avatar,
  AppBar,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Home as HomeIcon,
  Visibility as VisionIcon,
  Analytics as AnalyticsIcon,
  Flag as ObjectiveIcon,
  Assignment as ComponentIcon,
  People as ResourceIcon,
  Timeline as TimelineIcon,
  Warning as RiskIcon,
  Gavel as GovernanceIcon,
  Dashboard as PerformanceIcon,
  AutoAwesome as AdaptationIcon,
  DashboardCustomize as DashboardIcon,
  Add as NewIcon,
  FileDownload as ExportIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  CheckCircle as CheckIcon,
  TrackChanges as SmartGoalIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  SettingsBrightness as SystemModeIcon
} from '@mui/icons-material';

import useRoadmapStore from '../store/roadmapStore';
import useAuthStore from '../store/authStore';
import useThemeStore, { resolveMode } from '../store/themeStore';
import { STEP_LABELS } from '../utils/constants';

const DRAWER_WIDTH = 260;


export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const {
    roadmapData,
    getProgress,
    isStepCompleted,
    newProject,
    exportProject,
    setCurrentStep,
    currentRoadmapId,
    loadRoadmap,
    loadRoadmaps
  } = useRoadmapStore();
  const { user, logout } = useAuthStore();
  const themePreference = useThemeStore((state) => state.preference);
  const setThemePreference = useThemeStore((state) => state.setPreference);

  // On mount (or refresh), reload roadmap data from API if we have an ID but no data
  React.useEffect(() => {
    if (currentRoadmapId && !roadmapData.project_name) {
      loadRoadmap(currentRoadmapId);
    } else if (!currentRoadmapId) {
      loadRoadmaps();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const progress = getProgress();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    if (isMobile) setMobileOpen(false);
  };

  const handleNavigation = (path, step) => {
    navigate(path);
    if (step) setCurrentStep(step);
    if (isMobile) setMobileOpen(false);
  };

  const handleNewProject = () => {
    if (window.confirm(t('confirmations.newProject'))) {
      newProject();
      navigate('/home');
      if (isMobile) setMobileOpen(false);
    }
  };

  // Home + 10 roadmap steps (numbered) + Dashboard
  const navigationItems = [
    { path: '/home',       label: t('navigation.home'),                    icon: <HomeIcon />,        step: null,                  num: null },
    { path: '/vision',     label: t(STEP_LABELS.vision),                   icon: <VisionIcon />,      step: 'vision',              num: 1 },
    { path: '/situation',  label: t(STEP_LABELS.situation_analysis),       icon: <AnalyticsIcon />,   step: 'situation_analysis',  num: 2 },
    { path: '/objectives', label: t(STEP_LABELS.strategic_objectives),     icon: <ObjectiveIcon />,   step: 'strategic_objectives',num: 3 },
    { path: '/components', label: t(STEP_LABELS.action_components),        icon: <ComponentIcon />,   step: 'action_components',   num: 4 },
    { path: '/resources',  label: t(STEP_LABELS.resources),                icon: <ResourceIcon />,    step: 'resources',           num: 5 },
    { path: '/timeline',   label: t(STEP_LABELS.timeline),                 icon: <TimelineIcon />,    step: 'timeline',            num: 6 },
    { path: '/risks',      label: t(STEP_LABELS.risks),                    icon: <RiskIcon />,        step: 'risks',               num: 7 },
    { path: '/governance', label: t(STEP_LABELS.governance),               icon: <GovernanceIcon />,  step: 'governance',          num: 8 },
    { path: '/performance',label: t(STEP_LABELS.performance),              icon: <PerformanceIcon />, step: 'performance',         num: 9 },
    { path: '/adaptation', label: t(STEP_LABELS.adaptation),               icon: <AdaptationIcon />,  step: 'adaptation',          num: 10 },
    { path: '/dashboard',  label: t('navigation.dashboard'),               icon: <DashboardIcon />,   step: null,                  num: null },
    { path: '/smart-goals',label: t('smartGoals.navLabel'),                icon: <SmartGoalIcon />,   step: null,                  num: null },
  ];

  const languageOptions = [
    { value: 'en', label: 'EN' },
    { value: 'it', label: 'IT' },
    { value: 'fr', label: 'FR' }
  ];

  const themeOptions = [
    { value: 'dark',   label: t('theme.dark'),   icon: <DarkModeIcon sx={{ fontSize: 14 }} /> },
    { value: 'light',  label: t('theme.light'),  icon: <LightModeIcon sx={{ fontSize: 14 }} /> },
    { value: 'system', label: t('theme.system'), icon: <SystemModeIcon sx={{ fontSize: 14 }} /> }
  ];

  const completedCount = navigationItems.filter(i => i.step && isStepCompleted(i.step)).length;

  // ─── Sidebar content ───────────────────────────────────────────────────────
  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'var(--surface)',
        overflow: 'hidden'
      }}
    >
      {/* ── Logo ── */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--line-1)'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 14,
              color: '#fff',
              letterSpacing: 0.5,
              flexShrink: 0
            }}
          >
            RM
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '0.62rem',
                fontWeight: 700,
                color: 'var(--fg-faint)',
                textTransform: 'uppercase',
                letterSpacing: 1.5,
                lineHeight: 1
              }}
            >
              Strategic
            </Typography>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                color: 'var(--fg-strong)',
                lineHeight: 1.2
              }}
            >
              Roadmap
            </Typography>
          </Box>
        </Stack>
        {isMobile && (
          <IconButton
            size="small"
            onClick={() => setMobileOpen(false)}
            sx={{ color: 'var(--fg-muted)', '&:hover': { color: 'var(--fg-strong)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* ── Project + Progress ── */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid var(--line-1)' }}>
        {roadmapData.project_name ? (
          <Typography
            sx={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: 'var(--fg-strong)',
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {roadmapData.project_name}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '0.8rem', color: 'var(--fg-faint)', mb: 1, fontStyle: 'italic' }}>
            No project open
          </Typography>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-muted)', fontWeight: 500 }}>
            {completedCount} of 10 steps complete
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8rem',
              fontWeight: 700,
              color: '#6366f1'
            }}
          >
            {progress}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 5,
            borderRadius: 10,
            bgcolor: 'var(--line-1)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 10,
              background: progress >= 75
                ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                : progress >= 40
                  ? 'linear-gradient(90deg, #6366f1, #3b82f6)'
                  : 'linear-gradient(90deg, #6366f1, #8b5cf6)'
            }
          }}
        />
      </Box>

      {/* ── Navigation ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1.5,
          px: 1.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(99,102,241,0.4)',
            borderRadius: 10
          }
        }}
      >
        {/* Home */}
        {(() => {
          const item = navigationItems[0];
          const selected = location.pathname === item.path;
          return (
            <NavItem
              key={item.path}
              item={item}
              selected={selected}
              completed={false}
              onClick={() => handleNavigation(item.path, item.step)}
            />
          );
        })()}

        {/* Steps divider */}
        <Typography
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            color: 'var(--fg-faint)',
            textTransform: 'uppercase',
            letterSpacing: 1.2,
            px: 1.5,
            pt: 2,
            pb: 0.75
          }}
        >
          Steps
        </Typography>

        {/* Steps 1–10 */}
        {navigationItems.slice(1, 11).map((item) => {
          const selected = location.pathname === item.path;
          const completed = isStepCompleted(item.step);
          return (
            <NavItem
              key={item.path}
              item={item}
              selected={selected}
              completed={completed}
              onClick={() => handleNavigation(item.path, item.step)}
            />
          );
        })}

        {/* Tools divider */}
        <Box sx={{ pt: 1.5, pb: 0.5, px: 1.5 }}>
          <Divider sx={{ borderColor: 'var(--line-1)' }} />
        </Box>
        <Typography
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            color: 'var(--fg-faint)',
            textTransform: 'uppercase',
            letterSpacing: 1.2,
            px: 1.5,
            pt: 1,
            pb: 0.75
          }}
        >
          Tools
        </Typography>

        {/* Dashboard + SMART Goals */}
        {navigationItems.slice(11).map((item) => (
          <NavItem
            key={item.path}
            item={item}
            selected={location.pathname === item.path}
            completed={false}
            onClick={() => handleNavigation(item.path, item.step)}
          />
        ))}
      </Box>

      {/* ── Bottom bar ── */}
      <Box
        sx={{
          borderTop: '1px solid var(--line-1)',
          px: 2,
          pt: 1.5,
          pb: 2
        }}
      >
        {/* Quick Actions */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Tooltip title={t('actions.newProject')} placement="top">
            <Box
              component="button"
              onClick={handleNewProject}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                py: 1,
                px: 1.5,
                borderRadius: 1.5,
                border: '1px solid rgba(99,102,241,0.4)',
                background: 'rgba(99,102,241,0.12)',
                color: 'var(--accent-fg)',
                cursor: 'pointer',
                fontSize: '0.78rem',
                fontWeight: 600,
                transition: 'all 0.15s ease',
                '&:hover': {
                  background: 'rgba(99,102,241,0.22)',
                  borderColor: 'rgba(99,102,241,0.6)',
                  color: 'var(--accent-fg)'
                }
              }}
            >
              <NewIcon sx={{ fontSize: 16 }} />
              New
            </Box>
          </Tooltip>
          <Tooltip title={t('actions.exportReport')} placement="top">
            <Box
              component="button"
              onClick={() => exportProject()}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                py: 1,
                px: 1.5,
                borderRadius: 1.5,
                border: '1px solid var(--track)',
                background: 'transparent',
                color: 'var(--fg-muted)',
                cursor: 'pointer',
                fontSize: '0.78rem',
                fontWeight: 600,
                transition: 'all 0.15s ease',
                '&:hover': {
                  background: 'var(--track)',
                  borderColor: 'var(--line-soft)',
                  color: 'var(--fg-strong)'
                }
              }}
            >
              <ExportIcon sx={{ fontSize: 16 }} />
              Export
            </Box>
          </Tooltip>
        </Stack>

        {/* Language */}
        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
          {languageOptions.map((lang) => (
            <Chip
              key={lang.value}
              label={lang.label}
              size="small"
              clickable
              onClick={() => handleLanguageChange(lang.value)}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: 0.5,
                bgcolor: i18n.language === lang.value
                  ? 'rgba(99,102,241,0.25)'
                  : 'var(--line-1)',
                color: i18n.language === lang.value
                  ? 'var(--accent-fg)'
                  : 'var(--fg-faint)',
                border: `1px solid ${i18n.language === lang.value
                  ? 'rgba(99,102,241,0.5)'
                  : 'var(--track)'}`,
                '& .MuiChip-label': { px: 1 },
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: 'rgba(99,102,241,0.15)',
                  borderColor: 'rgba(99,102,241,0.4)'
                }
              }}
            />
          ))}
        </Stack>

        {/* Appearance */}
        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
          {themeOptions.map((option) => {
            const active = themePreference === option.value;
            return (
              <Tooltip key={option.value} title={option.label} placement="top">
                <Chip
                  icon={option.icon}
                  size="small"
                  clickable
                  aria-label={option.label}
                  aria-pressed={active}
                  onClick={() => setThemePreference(option.value)}
                  sx={{
                    flex: 1,
                    height: 24,
                    bgcolor: active ? 'rgba(99,102,241,0.25)' : 'var(--line-1)',
                    color: active ? 'var(--accent-fg)' : 'var(--fg-faint)',
                    border: `1px solid ${active ? 'rgba(99,102,241,0.5)' : 'var(--track)'}`,
                    '& .MuiChip-icon': { ml: 0, mr: 0, color: 'inherit' },
                    '& .MuiChip-label': { px: 0 },
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: 'rgba(99,102,241,0.15)',
                      borderColor: 'rgba(99,102,241,0.4)'
                    }
                  }}
                />
              </Tooltip>
            );
          })}
        </Stack>

        {/* User row */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: 'var(--line-1)',
            border: '1px solid var(--line-1)'
          }}
        >
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: '#6366f1',
              fontSize: '0.8rem',
              fontWeight: 700,
              flexShrink: 0
            }}
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'var(--fg-strong)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3
              }}
            >
              {user?.name || 'User'}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.68rem',
                color: 'var(--fg-faint)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2
              }}
            >
              {user?.email}
            </Typography>
          </Box>
          <Tooltip title="Logout" placement="top">
            <IconButton
              size="small"
              onClick={handleLogout}
              sx={{
                color: 'var(--fg-faint)',
                flexShrink: 0,
                '&:hover': { color: 'var(--status-red)', bgcolor: 'rgba(239,68,68,0.1)' }
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>

      {/* Mobile Top AppBar */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: 'var(--surface)',
            color: 'var(--fg-strong)',
            borderBottom: '1px solid var(--line-2)',
            zIndex: (theme) => theme.zIndex.drawer + 1
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: '56px !important', px: 2 }}>
            <IconButton
              size="small"
              onClick={() => setMobileOpen(true)}
              sx={{
                color: 'var(--status-slate)',
                border: '1px solid var(--line-2)',
                borderRadius: 1.5,
                '&:hover': { color: 'var(--fg-strong)', borderColor: 'rgba(99,102,241,0.4)' }
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: 11,
                  color: '#fff'
                }}
              >
                RM
              </Box>
              <Typography sx={{ fontWeight: 700, color: 'var(--fg-strong)', fontSize: '0.95rem' }}>
                Roadmap
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                size="small"
                aria-label={t('theme.toggle')}
                onClick={() =>
                  setThemePreference(resolveMode(themePreference) === 'dark' ? 'light' : 'dark')
                }
                sx={{
                  color: 'var(--fg-faint)',
                  border: '1px solid var(--line-2)',
                  borderRadius: 1.5,
                  '&:hover': { color: 'var(--fg-strong)', borderColor: 'rgba(99,102,241,0.4)' }
                }}
              >
                {resolveMode(themePreference) === 'dark'
                  ? <LightModeIcon sx={{ fontSize: 18 }} />
                  : <DarkModeIcon sx={{ fontSize: 18 }} />}
              </IconButton>
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: '#6366f1',
                  fontSize: '0.8rem',
                  fontWeight: 700
                }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </Stack>
          </Toolbar>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 2,
              bgcolor: 'var(--line-1)',
              '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #6366f1, #3b82f6)' }
            }}
          />
        </AppBar>
      )}

      {/* Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: '80vw',
              maxWidth: 300,
              boxSizing: 'border-box',
              border: 'none',
              bgcolor: 'var(--surface)'
            }
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              border: 'none',
              boxShadow: '1px 0 0 var(--line-1)'
            }
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 0,                                           /* pages handle own horizontal padding */
          pt: isMobile ? '64px' : 3,                      /* 56px AppBar + 8px gap (was 72px) */
          pb: { xs: 3, md: 4 },
          width: isMobile ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`,
          minHeight: '100dvh',
          overflowX: 'hidden',
          background: 'var(--page-bg)',
          ml: isMobile ? 0 : `${DRAWER_WIDTH}px`
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

// ─── Reusable nav item ────────────────────────────────────────────────────────
function NavItem({ item, selected, completed, onClick }) {
  return (
    <ListItem disablePadding sx={{ mb: 0.25 }}>
      <ListItemButton
        selected={selected}
        onClick={onClick}
        sx={{
          borderRadius: 1.5,
          py: 0.9,
          px: 1.5,
          minHeight: 40,
          position: 'relative',
          transition: 'all 0.15s ease',
          bgcolor: selected ? 'rgba(99,102,241,0.15)' : 'transparent',
          border: selected ? '1px solid rgba(99,102,241,0.25)' : '1px solid transparent',
          '&:hover': {
            bgcolor: selected ? 'rgba(99,102,241,0.2)' : 'var(--line-1)',
            borderColor: selected ? 'rgba(99,102,241,0.35)' : 'var(--line-2)'
          },
          // Left accent bar for active
          ...(selected && {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '20%',
              bottom: '20%',
              width: 3,
              borderRadius: '0 2px 2px 0',
              background: 'linear-gradient(180deg, #6366f1, #8b5cf6)'
            }
          })
        }}
      >
        {/* Step number badge or icon */}
        <ListItemIcon
          sx={{
            minWidth: 32,
            color: completed
              ? '#22c55e'
              : selected
                ? 'var(--accent-fg)'
                : 'var(--fg-faint)',
            transition: 'color 0.15s ease'
          }}
        >
          {item.num !== null ? (
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: completed
                  ? 'rgba(34,197,94,0.15)'
                  : selected
                    ? 'rgba(99,102,241,0.25)'
                    : 'var(--line-1)',
                border: `1px solid ${completed
                  ? 'rgba(34,197,94,0.4)'
                  : selected
                    ? 'rgba(99,102,241,0.4)'
                    : 'var(--line-2)'}`,
                color: completed
                  ? '#22c55e'
                  : selected
                    ? 'var(--accent-fg)'
                    : 'var(--fg-faint)',
                flexShrink: 0
              }}
            >
              {completed ? <CheckIcon sx={{ fontSize: 13 }} /> : item.num}
            </Box>
          ) : (
            React.cloneElement(item.icon, { sx: { fontSize: 20 } })
          )}
        </ListItemIcon>

        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            sx: {
              fontSize: '0.845rem',
              fontWeight: selected ? 600 : 400,
              color: selected ? 'var(--fg-strong)' : 'var(--fg-muted)',
              transition: 'color 0.15s ease',
              lineHeight: 1.3
            }
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}
